import { useMemo, useRef, useLayoutEffect } from "react";
import { motion } from "framer-motion";
import {
  ComposableMap,
  Geographies,
  Geography,
  ZoomableGroup
} from "react-simple-maps";
import countriesAtlas from "world-atlas/countries-50m.json";
import { feature } from "topojson-client";
import { gsap } from "gsap";

const allCountryFeatures = feature(
  countriesAtlas,
  countriesAtlas.objects.countries
).features;
const allCountryCollection = {
  type: "FeatureCollection",
  features: allCountryFeatures
};

const countryIdMap = {
  usa: "840",
  canada: "124",
  uk: "826",
  australia: "036",
  uae: "784",
  "new-zealand": "554",
  ireland: "372",
  germany: "276"
};

const europeIds = new Set([
  "008",
  "020",
  "040",
  "056",
  "070",
  "100",
  "112",
  "191",
  "196",
  "203",
  "208",
  "233",
  "246",
  "250",
  "276",
  "300",
  "348",
  "352",
  "372",
  "380",
  "428",
  "438",
  "440",
  "442",
  "470",
  "492",
  "498",
  "499",
  "528",
  "578",
  "616",
  "620",
  "642",
  "643",
  "674",
  "688",
  "703",
  "705",
  "724",
  "752",
  "756",
  "792",
  "804",
  "807",
  "826"
]);

const viewByCountry = {
  usa: { center: [-98, 38.5], zoom: 2.2 },
  canada: { center: [-97, 59], zoom: 1.95 },
  uk: { center: [-2.8, 54.8], zoom: 6.1 },
  australia: { center: [134, -25], zoom: 2.35 },
  uae: { center: [54.3, 24.2], zoom: 8.4 },
  "new-zealand": { center: [172.2, -41.3], zoom: 4.9 },
  europe: { center: [12, 53], zoom: 3.15 },
  ireland: { center: [-8.0, 53.3], zoom: 8.2 },
  germany: { center: [10.4, 51.1], zoom: 5.7 }
};

const accentByCountry = {
  usa: { from: "#2563EB", to: "#60A5FA", dot: "#1D4ED8" },
  canada: { from: "#0EA5E9", to: "#7DD3FC", dot: "#0369A1" },
  uk: { from: "#4F46E5", to: "#818CF8", dot: "#3730A3" },
  australia: { from: "#0891B2", to: "#67E8F9", dot: "#155E75" },
  uae: { from: "#D97706", to: "#FCD34D", dot: "#92400E" },
  "new-zealand": { from: "#0369A1", to: "#67E8F9", dot: "#0C4A6E" },
  europe: { from: "#1D4ED8", to: "#7DD3FC", dot: "#1E40AF" },
  ireland: { from: "#059669", to: "#6EE7B7", dot: "#065F46" },
  germany: { from: "#0284C7", to: "#67E8F9", dot: "#0C4A6E" }
};

function normalizeId(id) {
  if (id == null) {
    return "";
  }

  return String(id).padStart(3, "0");
}

function getFeaturesForMap(mapKey) {
  if (mapKey === "europe") {
    return allCountryFeatures.filter((item) => europeIds.has(normalizeId(item.id)));
  }

  const id = countryIdMap[mapKey];

  if (!id) {
    return [];
  }

  return allCountryFeatures.filter((item) => normalizeId(item.id) === id);
}

function CountryMapGraphic({ mapKey, mapGlow = "text-royal-blue" }) {
  const containerRef = useRef(null);
  const view = viewByCountry[mapKey] || viewByCountry.europe;
  const accent = accentByCountry[mapKey] || accentByCountry.europe;
  const selectedFeatures = useMemo(() => getFeaturesForMap(mapKey), [mapKey]);
  const selectedCollection = useMemo(
    () => ({
      type: "FeatureCollection",
      features: selectedFeatures
    }),
    [selectedFeatures]
  );
  const selectedIds = useMemo(
    () => new Set(selectedFeatures.map((item) => normalizeId(item.id))),
    [selectedFeatures]
  );

  useLayoutEffect(() => {
    if (!containerRef.current) {
      return undefined;
    }

    const ctx = gsap.context(() => {
      gsap.fromTo(
        ".map-highlight-fill",
        { opacity: 0.12, scale: 0.985, transformOrigin: "50% 50%" },
        { opacity: 1, scale: 1, duration: 1.05, ease: "power3.out" }
      );

      gsap.fromTo(
        ".map-highlight-outline",
        { strokeDashoffset: 220, opacity: 0.5 },
        { strokeDashoffset: 0, opacity: 1, duration: 1.25, ease: "power2.out", stagger: 0.03 }
      );

      gsap.to(".map-highlight-glow", {
        opacity: 0.92,
        duration: 1.9,
        repeat: -1,
        yoyo: true,
        ease: "sine.inOut"
      });
    }, containerRef);

    return () => ctx.revert();
  }, [mapKey]);

  return (
    <motion.div
      ref={containerRef}
      initial={{ opacity: 0, y: 12, scale: 0.98 }}
      animate={{ opacity: 1, y: 0, scale: 1 }}
      transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
      className="relative overflow-hidden rounded-[1.8rem] border border-white/40 bg-white/70 p-4 shadow-luxury backdrop-blur-md"
    >
      <div className="absolute -left-4 top-8 h-28 w-28 rounded-full bg-royal-blue/20 blur-3xl" />
      <div className="absolute -right-6 bottom-8 h-24 w-24 rounded-full bg-warm-gold/20 blur-3xl" />

      <div className="relative flex h-[300px] items-center justify-center rounded-[1.4rem] border border-midnight-navy/10 bg-gradient-to-br from-sky-glow/70 to-white p-3 md:h-[360px]">
        <motion.div
          className={`${mapGlow} h-full w-full`}
          animate={{ y: [0, -4, 0] }}
          transition={{ duration: 6, repeat: Infinity, ease: "easeInOut" }}
        >
          <ComposableMap
            width={820}
            height={520}
            projection="geoMercator"
            projectionConfig={{ scale: 145 }}
            style={{ width: "100%", height: "100%" }}
          >
            <defs>
              <linearGradient id={`map-gradient-${mapKey}`} x1="0%" y1="0%" x2="100%" y2="100%">
                <stop offset="0%" stopColor={accent.from} stopOpacity="0.62" />
                <stop offset="100%" stopColor={accent.to} stopOpacity="0.32" />
              </linearGradient>
              <filter id={`map-shadow-${mapKey}`} x="-50%" y="-50%" width="200%" height="200%">
                <feDropShadow dx="0" dy="0" stdDeviation="4" floodColor={accent.from} floodOpacity="0.45" />
              </filter>
            </defs>

            <ZoomableGroup center={view.center} zoom={view.zoom}>
              <Geographies geography={allCountryCollection}>
                {({ geographies }) =>
                  geographies.map((geo) => {
                    const id = normalizeId(geo.id);
                    const isSelected = selectedIds.has(id);

                    return (
                      <Geography
                        key={`base-${geo.rsmKey}`}
                        geography={geo}
                        style={{
                          default: {
                            fill: isSelected ? `url(#map-gradient-${mapKey})` : "rgba(15, 23, 42, 0.03)",
                            stroke: isSelected ? accent.from : "rgba(15, 23, 42, 0.18)",
                            strokeWidth: isSelected ? 0.95 : 0.55,
                            outline: "none",
                            filter: isSelected ? `url(#map-shadow-${mapKey})` : "none"
                          },
                          hover: {
                            fill: isSelected ? `url(#map-gradient-${mapKey})` : "rgba(15, 23, 42, 0.05)",
                            stroke: isSelected ? accent.from : "rgba(15, 23, 42, 0.24)",
                            strokeWidth: isSelected ? 1 : 0.6,
                            outline: "none"
                          },
                          pressed: {
                            fill: isSelected ? `url(#map-gradient-${mapKey})` : "rgba(15, 23, 42, 0.05)",
                            stroke: isSelected ? accent.from : "rgba(15, 23, 42, 0.24)",
                            strokeWidth: isSelected ? 1 : 0.6,
                            outline: "none"
                          }
                        }}
                        className={isSelected ? "map-highlight-fill" : "map-base-shape"}
                      />
                    );
                  })
                }
              </Geographies>

              <Geographies geography={selectedCollection}>
                {({ geographies }) =>
                  geographies.map((geo) => (
                    <Geography
                      key={`outline-${geo.rsmKey}`}
                      geography={geo}
                      className="map-highlight-outline map-highlight-glow"
                      style={{
                        default: {
                          fill: "none",
                          stroke: accent.from,
                          strokeWidth: 1.5,
                          strokeDasharray: "220 220",
                          outline: "none"
                        },
                        hover: {
                          fill: "none",
                          stroke: accent.from,
                          strokeWidth: 1.5,
                          strokeDasharray: "220 220",
                          outline: "none"
                        },
                        pressed: {
                          fill: "none",
                          stroke: accent.from,
                          strokeWidth: 1.5,
                          strokeDasharray: "220 220",
                          outline: "none"
                        }
                      }}
                    />
                  ))
                }
              </Geographies>
            </ZoomableGroup>
          </ComposableMap>
        </motion.div>
      </div>
    </motion.div>
  );
}

export default CountryMapGraphic;
